/**
 * @CODE:ROOMS-HOOKS-001
 * useRooms Hook
 *
 * Custom hook for fetching rooms for a specific accommodation
 * Features:
 * - Fetches rooms by accommodation_id using TanStack Query
 * - 5 minute staleTime for caching
 * - Automatic refetch on accommodation_id change
 * - Loading and error state management
 */

import { useQuery } from "@tanstack/react-query";
import { getRoomsByAccommodationId } from "../api/rooms.api";
import type { Room } from "../types";

interface UseRoomsReturn {
  data: Room[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Hook to fetch rooms by accommodation ID
 * @param accommodationId - The accommodation ID (UUID) to fetch rooms for
 * @returns Object with rooms data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: rooms, isLoading, isError } = useRooms(accommodationId);
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (isError) return <ErrorMessage />;
 *
 * return (
 *   <div>
 *     {rooms?.map(room => (
 *       <RoomCard key={room.id} room={room} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useRooms(accommodationId: string): UseRoomsReturn {
  const query = useQuery({
    queryKey: ["rooms", accommodationId],
    queryFn: async () => {
      const response = await getRoomsByAccommodationId(accommodationId);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    enabled: !!accommodationId, // Only run query if accommodationId is provided
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
  };
}
